export interface IntRangeModel {
  start: number;
  end: number;
  inclusiveStart: boolean;
  inclusiveEnd: boolean;
}

export class IntRange {
  public start: number;

  public end: number;

  public inclusiveStart: boolean = true;

  public inclusiveEnd: boolean = false;

  constructor(
    start: number,
    end: number,
    inclusiveStart: boolean = true,
    inclusiveEnd: boolean = false,
  ) {
    this.start = start;
    this.end = end;
    this.inclusiveStart = inclusiveStart;
    this.inclusiveEnd = inclusiveEnd;
  }

  /**
   * Instantiate the int-range class using the object
   * returned in the api response
   */
  public static fromObject(data: IntRangeModel): IntRange {
    return new IntRange(
      data.start,
      data.end,
      data.inclusiveStart,
      data.inclusiveEnd,
    );
  }

  public static fromDto(dto: any): IntRange {
    return new IntRange(
      dto.start,
      dto.end,
      dto.inclusiveStart,
      dto.inclusiveEnd,
    );
  }

  /**
   * Either include or exclude the start from the range
   */
  public get min(): number {
    if (this.inclusiveStart) {
      return this.start;
    }

    return this.start + 1;
  }

  /**
   * Either include or exclude the end from the range
   */
  public get max(): number {
    if (this.inclusiveEnd) {
      return this.end;
    }

    return this.end - 1;
  }

  /**
   * Returns true if the given value falls outside of this range.
   */
  public outOfRange(value: number): boolean {
    return value < this.min || value > this.max;
  }

  /**
   * calculate how many item are in the range
   *
   * 1. determine the start value
   * @see IntRange.min
   * 2. determine the end value
   * @see IntRange.max
   *
   * example/proof:
   * :range: === :items:   === :item_count: <> :min/max_item: === :calculation:
   * [0-5] === 0 1 2 3 4 5 === 6            <> 0-5            === 5-0 = 5 +1 = 6
   * [0-5) === 0 1 2 3 4   === 5            <> 0-4            === 4-0 = 4 +1 = 5
   * (0-5] === 1 2 3 4 5   === 5            <> 1-5            === 5-1 = 4 +1 = 5
   * (0-5) === 1 2 3 4     === 4            <> 1-4            === 4-1 = 3 +1 = 4
   *
   * [1-6] === 1 2 3 4 5 6 === 6            <> 1-6            === 6-1 = 5 +1 = 6
   * [1-6) === 1 2 3 4 5   === 5            <> 1-5            === 5-1 = 4 +1 = 5
   * (1-6] === 2 3 4 5 6   === 5            <> 2-6            === 6-2 = 4 +1 = 5
   * (1-6) === 2 3 4 5     === 4            <> 2-5            === 5-2 = 3 +1 = 4
   */
  public totalSteps(): number {
    return this.max - this.min + 1;
  }
}
